import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import {
    GetDncStatus,
    DncStatusResponse,
    CreateContactAttempt,
    CreateContactAttemptParams,
    DncQueryParams,
    DncStatusApiResponse,
    EmailPreferences,
    PhonePreferences,
    PhoneContactPreferences,
    EmailContactPreferences
} from '../actions/compliance_actions';
import { Strings } from '../assets/common/strings';
import { http } from '../utilities';

function* createContactAttemptStatusSaga(action: ReturnType<typeof CreateContactAttempt.started>) {
    const params = action.payload;    
    try {
        const response: Response = yield call(
            complianceClient.createContactAttempt,
            params
        );

        if (!response.ok) {
            const text = yield response.text();
            throw new Error(`HTTP ${response.status}: ${text}`);
        }
        
        yield put(CreateContactAttempt.done({
            params: action.payload,
            result: undefined
        }));

    } catch (error) {
        yield put(CreateContactAttempt.failed({
            params: action.payload,
            error: error instanceof Error
                ? error
                : new Error(JSON.stringify(error))
            }));
    }
}

function* getDncStatusSaga(action: ReturnType<typeof GetDncStatus.started>) {
    const params = action.payload;
    try {
        const response = yield call(
            complianceClient.getDncStatus, 
            params
        );

        const text = yield response.text();

        if (!response.ok) {
            throw new Error(`HTTP ${response.status} : ${text}`);
        }
        
        const apiData: DncStatusApiResponse = JSON.parse(text);

        const data: DncStatusResponse = {
            phone: params.phone,
            canContact: apiData.ContactStatus?.CanContact ?? false,
            isDnc: apiData.IsDoNotCall,
            reason: apiData.ContactStatus?.Reason ?? ''
        }
        
        yield put(
            GetDncStatus.done({ 
                params, 
                result: data 
            })
        );        
    } catch (error) {
        yield put(
            GetDncStatus.failed({ 
                params, 
                error: error instanceof Error 
                    ? error 
                    : new Error(JSON.stringify(error))                
            })
        );
    }
}


// Fire-and-forget saga:
// Preference updates do not impact UI state.
// We only log errors if the call fails.
function* phonePreferencesSaga(action: ReturnType<typeof PhoneContactPreferences.started>) {
    try {
        const response: Response = yield call(
            complianceClient.updatePhonePreferences,
            action.payload
        );

        if (!response.ok) {
            const text = yield response.text();
            throw new Error(`HTTP ${response.status} : ${text}`);
        }

    } catch (error) {
        console.error(
            'Setting phone preferences failed',
            error instanceof Error ? error.message : error
        );
    }
}

function* emailPreferencesSaga(action: ReturnType<typeof EmailContactPreferences.started>) {
    try {
        const response: Response = yield call(
            complianceClient.updateEmailPreferences,
            action.payload
        );

        if (!response.ok) {
            const text = yield response.text();
            throw new Error(`HTTP ${response.status} : ${text}`);
        }

    } catch (error) {
        console.error(
            'Setting email preferences failed',
            error instanceof Error ? error.message : error
        );
    }
}

// --- Watchers ---
export function* complianceSagas() {
  yield takeEvery(GetDncStatus.started, getDncStatusSaga);
  yield takeLatest(CreateContactAttempt.started, createContactAttemptStatusSaga);
  yield takeEvery(PhoneContactPreferences.started, phonePreferencesSaga);
  yield takeEvery(EmailContactPreferences.started, emailPreferencesSaga);
}

const complianceClient = {
    getDncStatus(
        params: DncQueryParams
    ) : Promise<Response> {
        const query = new URLSearchParams({
            phone: params.phone,
            hasCallback: String(params.hasCallback),
            ...(params.zipCode && { zipCode: params.zipCode }),
            ...(params.businessOwner && { businessOwner: params.businessOwner }),
            ...(params.createdOn && { createdOn: params.createdOn }),
            ...(params.agentCode && { agentCode: params.agentCode })
        }).toString();

        return http(
            `compliance/dnc-status?${query}`,
            { method: Strings.Http.Get }
        );
    },

    createContactAttempt(
        params: CreateContactAttemptParams
    ) : Promise<Response> {
        const query = new URLSearchParams({
            phone: params.phone,
            hasCallback: String(params.hasCallback),
            ...(params.zipCode && { zipCode: params.zipCode })
        }).toString();

        return http(
            `compliance/contact-attempts/status?${query}`,
            { method: Strings.Http.Post }
        );
    },

    updatePhonePreferences(
        params: PhonePreferences
    ) : Promise<Response> {
        return http('compliance/phone/preferences',
            {
                method: Strings.Http.Post,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    phoneNumber: params.phoneNumber,
                    doNotCall: params.doNotCall,
                    doNotText: params.doNotText
                })
            }
        );
    },

    updateEmailPreferences(
        params: EmailPreferences
    ) : Promise<Response> {
        return http('compliance/email/preferences',
            {
                method: Strings.Http.Post,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    emailAddress: params.emailAddress,
                    doNotEmail: params.doNotEmail
                })
            }
        );
    }
}

